import { useState } from "react";
import ScrollContainer from "react-perfect-scrollbar";
import { useForm } from "react-hook-form";
import * as Sentry from "@sentry/nextjs";
import { getUnixTime, parse } from "date-fns";
import ModalCloseIcon from "~/assets/images/_modal__close.svg";
import { PageTitle } from "~/components/aud/ui/PageTitle";
import { FormSubmitButton } from "~/components/FormSubmitButton";
import { useToast } from "~/utils";
import { Analytics } from "~/components/GoogleAnalytics";
import { setAuthToken } from "~/authtoken";
import { useUser } from "~/context/userContext";
import { isCommunicativeAge } from "~/functions/user";
import { EmailField } from "~/components/settings/EmailField";
import { NicknameField } from "~/components/settings/NicknameField";
import { YearOfBirthField } from "~/components/settings/YearOfBirthField";
import { PasswordField } from "~/components/settings/PasswordField";
import { AgreementField } from "~/components/settings/AgreementField";
import { SignUpInput, SignUpPayload } from "~/generated/rtk-query/nowdo-live-api.generated";
import css from "./index.module.scss";
import classnames from "classnames";
import { nowdoApi, useSignUpMutation } from "~/hooks/rtk-query-api";
import { AppStore } from "~/store";
import { useStore } from "react-redux";

type Props = {
  open: boolean;
  onClose: () => unknown;
  onSuccess: () => unknown;
};

export const SignUpModal: React.FC<Props> = ({ open, onSuccess, onClose }) => {
  const now = new Date();
  const store: AppStore = useStore();
  const toast = useToast();
  const { resetUser } = useUser();
  const [loading, setLoading] = useState(false);
  const [signUp] = useSignUpMutation();

  const {
    register,
    handleSubmit,
    getValues,
    formState: { errors },
  } = useForm({
    defaultValues: {
      email: "",
      nickname: "",
      yearOfBirth: 1991,
      password: "",
      agreement: false,
    },
  });

  if (!open) {
    return null;
  }

  const onSubmit = async (data) => {
    setLoading(true);

    try {
      const parsedDate = parse(`${data.yearOfBirth}-12-31 23:59:59`, "yyyy-MM-dd HH:mm:ss", new Date());

      const input = {
        email: data.email,
        password: data.password,
        birthDate: getUnixTime(parsedDate),
        nickname: data.nickname,
      } as SignUpInput;

      const payload: SignUpPayload = await signUp({
        signUpInput: input,
      }).unwrap();

      setAuthToken({}, payload.token);
      await store.dispatch(nowdoApi.util.resetApiState());

      const { data: user } = await store.dispatch(nowdoApi.endpoints.getMe.initiate());
      if (!user) {
        toast.error("エラーが発生しました。お手数ですがもう一度時間を開けておためしください。");
        return;
      }

      resetUser(user);
      store.dispatch(nowdoApi.util.resetApiState());

      Analytics({ action: "click_signup_success", label: "SignUp" });

      onClose();
      onSuccess();
    } catch (e) {
      Analytics({ action: "click_signup_failed", label: "SignUp" });
      Sentry.captureException(e);
      if (e.data?.error && e.status === 422) {
        toast.error(e.data.error);
        return;
      }
      toast.error("エラーが発生しました。お手数ですがもう一度時間を開けておためしください。");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="_modal__wrapper show">
      <ScrollContainer>
        <div className="_modal wide">
          <div className="content">
            <PageTitle title="Welcome  NowDo!" sub="NowDoに参加する" />
            <form className={classnames(css.form, "_form")} onSubmit={handleSubmit(onSubmit)}>
              <EmailField
                register={register}
                errors={errors}
                onChange={() => Analytics({ action: "fill_email", label: "SignUp" })}
              />
              <NicknameField register={register} errors={errors} />
              <YearOfBirthField now={now} register={register} errors={errors} />
              <PasswordField register={register} errors={errors} />
              <AgreementField register={register} errors={errors} />
              {!isCommunicativeAge(`${getValues("yearOfBirth")}-12-31`) && (
                <p>
                  <span className="sub red">
                    年齢が12才に満たない場合はコミュニティ機能はご利用できません。
                    <br />
                    ライブ配信のサブスクリプションや動画の視聴は保護者の方の同意を得てご利用ください。
                  </span>
                </p>
              )}
              <p>
                <FormSubmitButton loading={loading} size="medium">
                  新規登録
                </FormSubmitButton>
              </p>
            </form>
            <div className="_modal__close" onClick={onClose}>
              <ModalCloseIcon />
            </div>
          </div>
        </div>
      </ScrollContainer>
    </div>
  );
};
